import * as React from 'react'

import { Input, type InputProps } from '@/components/ui/input'
import {
  formatCurrencyInputValue,
  parseCurrencyInput,
  sanitizeCurrencyDraft,
} from '@/lib/format'
import { cn } from '@/lib/utils'

export type CurrencyInputProps = Omit<
  InputProps,
  'type' | 'inputMode' | 'value' | 'onChange' | 'defaultValue'
> & {
  value: string
  onValueChange: (raw: string) => void
  /** When true, wraps the field with a non-editable € prefix. */
  showEuroPrefix?: boolean
  /** Optional typography override for the non-editable € prefix. */
  prefixClassName?: string
  /** Allow a leading minus. Defaults to false (money fields are non-negative). */
  allowNegative?: boolean
}

/**
 * Text currency field that accepts both `.` and `,` as decimal separators.
 * Keeps the raw string so a typed comma is not lost mid-edit; on blur the
 * value is rounded to 2 decimals and shown in French format
 */
const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      value,
      onValueChange,
      showEuroPrefix = false,
      prefixClassName,
      allowNegative = false,
      className,
      onBlur,
      placeholder = '0,00',
      ...props
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = sanitizeCurrencyDraft(e.target.value, allowNegative)
      if (next === null) return
      onValueChange(next)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseCurrencyInput(value)
      if (parsed !== null) {
        const rounded = formatCurrencyInputValue(parsed)
        if (rounded !== value) onValueChange(rounded)
      }
      onBlur?.(e)
    }

    const input = (
      <Input
        {...props}
        ref={ref}
        type='text'
        inputMode='decimal'
        autoComplete='off'
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn(className, showEuroPrefix && 'pl-10')}
      />
    )

    if (!showEuroPrefix) return input

    return (
      <div className='relative'>
        <span
          className={cn(
            'pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-base font-medium leading-none text-[#272c36]',
            prefixClassName
          )}
          aria-hidden='true'
        >
          €
        </span>
        {input}
      </div>
    )
  }
)
CurrencyInput.displayName = 'CurrencyInput'

export type CurrencyNumberInputProps = Omit<
  CurrencyInputProps,
  'value' | 'onValueChange'
> & {
  value: number | undefined
  onValueChange: (amount: number | undefined) => void
  /** When the field is cleared, emit `0` instead of `undefined`. Default true. */
  emptyAsZero?: boolean
}

/**
 * Number-backed currency field. Holds a local draft string while focused so `,`
 * survives mid-edit; when blurred, displays the formatted value from props.
 */
function CurrencyNumberInput({
  value,
  onValueChange,
  emptyAsZero = true,
  ...props
}: CurrencyNumberInputProps) {
  const [draft, setDraft] = React.useState(() => formatDraftFromNumber(value))
  const [focused, setFocused] = React.useState(false)

  // When not editing, mirror the parent number (no effect / cascading setState).
  const displayValue = focused ? draft : formatDraftFromNumber(value)

  return (
    <CurrencyInput
      {...props}
      value={displayValue}
      onFocus={e => {
        setDraft(formatDraftFromNumber(value))
        setFocused(true)
        props.onFocus?.(e)
      }}
      onBlur={e => {
        setFocused(false)
        props.onBlur?.(e)
      }}
      onValueChange={raw => {
        setDraft(raw)
        if (raw === '' || raw === '-' || raw === '.' || raw === ',') {
          onValueChange(emptyAsZero ? 0 : undefined)
          return
        }
        const parsed = parseCurrencyInput(raw)
        if (parsed !== null) onValueChange(parsed)
      }}
    />
  )
}

function formatDraftFromNumber(value: number | undefined): string {
  if (value === undefined) return ''
  return formatCurrencyInputValue(value)
}

export { CurrencyInput, CurrencyNumberInput }
